import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Icon,
  IconButton,
  useMediaQuery,
  SearchFiltersActions,
  SearchFiltersMobileDialog,
  SearchFiltersMobileTrigger,
  SearchFiltersSidebar
} from 'investira.react.components';
import { useComparadorFundosContext } from '../../../contexts/ComparadorFundosContext';
import services from '../../../services';
import {
  COMPARADOR_FUNDOS_SEARCH_PAGE_SIZE,
  buildComparadorFundosSearchParams,
  normalizeComparadorFundosSearchResults
} from '../_shared/comparadorFundosSearch';
import helpers from '../../../utils/helpers';
import ComparadorFundosFilters from './ComparadorFundosFilters';
import ComparadorFundosFocusedItem from './ComparadorFundosFocusedItem';
import ComparadorFundosResultsList from './ComparadorFundosResultsList';
import ComparadorFundosSortButton from './ComparadorFundosSortButton';

const wInitialFilters = {
  ativo_tipo_id: [],
  top_setor_id: '',
  fundo_estrategia_id: [],
  situacao: [],
  fechado: '',
  condominio_fechado: '',
  exclusivo: '',
  qualificado: '',
  profissional: '',
  esg: '',
  cotas: '',
  estruturado: '',
  benchmark_indicador_id: [],
  cvm_175: '',
  longo: '',
  taxa_adm: '',
  criado_a_mais_de: '',
  nao_padronizado: '',
  responsabilidade_limitada: '',
  publico_alvo: [],
  distribuicao: [],
  aplicacao_min: '',
  resgate_carencia: '',
  previdenciario: '',
  amostragem: '',
  retorno: '',
  perc_dias_acima_benchmark: '',
  perc_meses_acima_benchmark: '',
  sharpe: '',
  tracking_error: '',
  volatilidade: '',
  pl: '',
  cotistas: '',
  gestor_pl: '',
  gestor_cotistas: '',
  adm_pl: '',
  adm_cotistas: ''
};

const wVisibleFilterNames = [
  'ativo_tipo_id',
  'benchmark_indicador_id',
  'situacao',
  'exclusivo',
  'fechado',
  'longo',
  'cotas',
  'condominio_fechado',
  'responsabilidade_limitada',
  'qualificado',
  'profissional',
  'publico_alvo',
  'distribuicao',
  'previdenciario',
  'estruturado',
  'esg',
  'taxa_adm',
  'aplicacao_min',
  'resgate_carencia',
  'retorno',
  'perc_dias_acima_benchmark',
  'perc_meses_acima_benchmark',
  'sharpe',
  'tracking_error',
  'volatilidade',
  'pl',
  'cotistas',
  'gestor_pl',
  'adm_pl'
];

/** Cria cópias independentes para os arrays do estado controlado de filtros. */
function pvCloneFilters(pFilters) {
  return Object.entries(pFilters).reduce(
    (pResult, [pName, pValue]) => ({
      ...pResult,
      [pName]: Array.isArray(pValue) ? [...pValue] : pValue
    }),
    {}
  );
}

/** Conta somente filtros visíveis que já foram aplicados. */
function pvCountAppliedFilters(pFilters) {
  return wVisibleFilterNames.filter(pName => {
    const xValue = pFilters[pName];

    return Array.isArray(xValue) ? xValue.length > 0 : xValue !== '' && xValue != null;
  }).length;
}

/** Anexa uma página sem repetir fundos já carregados. */
function pvMergeResults(pCurrentResults, pNextResults) {
  const xResultsMap = new Map(
    [...pCurrentResults, ...pNextResults].map(pItem => [pItem.id, pItem])
  );

  return Array.from(xResultsMap.values());
}

/**
 * Diálogo local que combina lista inicial, pesquisa e filtros de fundos.
 */
const ComparadorFundosBuscarDialog = memo(({ open, maxSelected, onClose, onSelect }) => {
  const { state } = useComparadorFundosContext();
  const inputRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const currentSearchRef = useRef({});
  const nextPageRef = useRef(2);
  const loadingMoreRef = useRef(false);
  const [query, setQuery] = useState('');
  const [draftFilters, setDraftFilters] = useState(() => pvCloneFilters(wInitialFilters));
  const [appliedFilters, setAppliedFilters] = useState(() => pvCloneFilters(wInitialFilters));
  const [filterValidity, setFilterValidity] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sort, setSort] = useState('');
  const [results, setResults] = useState([]);
  const [focusedId, setFocusedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const selectedFundosIds = state.selectedFundosIds || [];
  const fullScreen = typeof window !== 'undefined' && window.innerWidth <= 600;
  const isUpMd = useMediaQuery(theme => theme.breakpoints.up('md'));
  const isUpLg = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const focusedItem = useMemo(
    () => results.find(pItem => pItem.id === focusedId) || null,
    [focusedId, results]
  );
  const reachedLimit = selectedFundosIds.length >= maxSelected;
  const appliedFiltersCount = useMemo(
    () => pvCountAppliedFilters(appliedFilters),
    [appliedFilters]
  );
  const applyDisabled = Object.values(filterValidity).some(pValid => pValid === false);

  /** Atualiza a validade conhecida de uma faixa do rascunho. */
  // function pvChangeFilterValidity(pName, pValid) {
  //   setFilterValidity(pCurrentValidity => ({ ...pCurrentValidity, [pName]: pValid }));
  // }

  const pvChangeFilterValidity = useCallback((pName, pValid) => {
    setFilterValidity(pCurrentValidity =>
      pCurrentValidity[pName] === pValid
        ? pCurrentValidity
        : { ...pCurrentValidity, [pName]: pValid }
    );
  }, []);
  /** Restaura apenas o rascunho para os valores iniciais do domínio. */
  function pvClearDraftFilters() {
    setDraftFilters(pvCloneFilters(wInitialFilters));
    setFilterValidity({});
  }

  /** Promove o rascunho válido e reinicia a busca pelo efeito de filtros aplicados. */
  function pvApplyDraftFilters() {
    if (applyDisabled) {
      return;
    }

    setAppliedFilters(pvCloneFilters(draftFilters));
    setMobileFiltersOpen(false);
  }

  /** Abre o painel móvel sempre a partir do último snapshot aplicado. */
  function pvOpenMobileFilters() {
    setDraftFilters(pvCloneFilters(appliedFilters));
    setFilterValidity({});
    setMobileFiltersOpen(true);
  }

  /** Descarta alterações não aplicadas feitas no painel móvel. */
  function pvCloseMobileFilters() {
    setDraftFilters(pvCloneFilters(appliedFilters));
    setFilterValidity({});
    setMobileFiltersOpen(false);
  }

  /** Carrega fundos e ignora a chamada quando uma busca mais recente a substituiu. */
  const pvLoadResults = useCallback(pRequest => {
    if (requestSequenceRef.current !== pRequest.sequence) {
      return;
    }

    services.pesquisas.ativo(
      buildComparadorFundosSearchParams(pRequest.search),
      pResponse => {
        if (requestSequenceRef.current === pRequest.sequence) {
          const xResults = normalizeComparadorFundosSearchResults(pResponse, pRequest.search.query);

          if (pRequest.append) {
            setResults(pCurrentResults => pvMergeResults(pCurrentResults, xResults));
          } else {
            setResults(xResults);
            setFocusedId(xResults[0]?.id || '');
          }

          nextPageRef.current = pRequest.search.page + 1;
          setHasMore(Boolean(pResponse?.pages?.next));
          setTotalItems(pResponse?.pages?.total_items ?? 0);
        }
      },
      () => {
        if (requestSequenceRef.current === pRequest.sequence) {
          if (pRequest.append) {
            setLoadMoreError('Não foi possível carregar mais fundos.');
          } else {
            setError('Não foi possível carregar fundos agora.');
            setHasMore(false);
          }
        }
      },
      () => {
        if (requestSequenceRef.current === pRequest.sequence) {
          if (pRequest.append) {
            loadingMoreRef.current = false;
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    );
  }, []);

  const pvLoadResultsDebounced = useMemo(
    () => helpers.debounce(pvLoadResults, 400),
    [pvLoadResults]
  );

  /** Invalida callbacks pendentes e cancela a busca ativa. */
  const pvInvalidateRequest = useCallback(() => {
    requestSequenceRef.current += 1;
    services.pesquisas.cancel?.ativo?.cancel?.();
  }, []);

  /** Atualiza a consulta e invalida imediatamente a busca que deixou de estar ativa. */
  function pvChangeQuery(pEvent) {
    const xQuery = pEvent.target.value;

    if (xQuery.trim().length < 2) {
      pvInvalidateRequest();
    }

    setQuery(xQuery);
  }

  /** Invalida a busca antes de fechar o diálogo. */
  function pvClose() {
    pvInvalidateRequest();
    pvCloseMobileFilters();
    onClose();
  }

  useEffect(() => {
    if (!open) {
      pvInvalidateRequest();
      setQuery('');
      setResults([]);
      setFocusedId('');
      setError('');
      setLoadMoreError('');
      setHasMore(false);
      setTotalItems(0);
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      return;
    }

    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open, pvInvalidateRequest]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const xSequence = requestSequenceRef.current + 1;
    const xShouldDebounce = query.trim().length >= 2;

    requestSequenceRef.current = xSequence;
    currentSearchRef.current = {
      ...appliedFilters,
      query,
      sort,
      page: 1,
      size: COMPARADOR_FUNDOS_SEARCH_PAGE_SIZE
    };
    nextPageRef.current = 2;
    loadingMoreRef.current = false;
    setLoading(true);
    setLoadingMore(false);
    setError('');
    setLoadMoreError('');
    setHasMore(false);
    setTotalItems(0);
    setResults([]);
    setFocusedId('');
    const xRequest = {
      search: currentSearchRef.current,
      append: false,
      sequence: xSequence
    };

    if (xShouldDebounce) {
      pvLoadResultsDebounced(xRequest);
    } else {
      pvLoadResults(xRequest);
    }

    return () => {
      pvInvalidateRequest();
    };
  }, [
    appliedFilters,
    open,
    pvInvalidateRequest,
    pvLoadResults,
    pvLoadResultsDebounced,
    query,
    sort
  ]);

  /** Carrega a página seguinte quando a lista alcança o fim. */
  const pvLoadMore = useCallback(() => {
    if (!open || loading || !hasMore || loadingMoreRef.current) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadMoreError('');
    pvLoadResults({
      search: {
        ...currentSearchRef.current,
        page: nextPageRef.current
      },
      append: true,
      sequence: requestSequenceRef.current
    });
  }, [hasMore, loading, open, pvLoadResults]);

  /** Seleciona diretamente o fundo clicado quando a seleção ainda é válida. */
  function pvSelectFundo(pItem) {
    if (!pItem || selectedFundosIds.includes(pItem.id) || reachedLimit) {
      return;
    }

    onSelect({
      ...pItem.raw,
      ifeed_ativo_id: pItem.id,
      ticker: pItem.ticker || null,
      ativo: pItem.ativo || null,
      cnpj: pItem.cnpj || null,
      gestor: pItem.gestor || null,
      ativoTipoId: pItem.ativoTipoId || null,
      logoPessoaId: pItem.logoPessoaId || null,
      logoOk: pItem.logoPessoaId || null,
      pessoa: pItem.pessoa || null
    });
    pvClose();
  }

  return (
    <Dialog open={open} onClose={pvClose} fullScreen={fullScreen} fullWidth maxWidth="xl">
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{
            height: { xs: '100vh', sm: 'calc(100vh - 64px)' },
            minHeight: 0,
            overflowY: { xs: 'auto', md: 'hidden' }
          }}>
          {isUpLg && (
            <>
              <Stack component="aside" sx={{ minWidth: 300, width: 300 }}>
                <SearchFiltersSidebar
                  title="Perfil"
                  actions={
                    <SearchFiltersActions
                      applyDisabled={applyDisabled}
                      onApply={pvApplyDraftFilters}
                      onClear={pvClearDraftFilters}
                    />
                  }>
                  <ComparadorFundosFilters
                    appliedFilters={appliedFilters}
                    filters={draftFilters}
                    onChange={setDraftFilters}
                    onValidityChange={pvChangeFilterValidity}
                  />
                </SearchFiltersSidebar>
              </Stack>
              <Divider orientation="vertical" flexItem />
            </>
          )}
          <Stack
            sx={{
              flexGrow: 1,
              minWidth: 400,
              // width: 600,
              minHeight: { xs: 600, md: 0 },
              height: { md: '100%' }
            }}
            alignItems="stretch">
            <Stack sx={{ px: 2, py: 4, pb: 1 }} direction="row" gap={1} alignItems="stretch">
              <Stack sx={{ flexGrow: 1 }}>
                <TextField
                  inputRef={inputRef}
                  label="Buscar fundo"
                  placeholder="Ticker, nome, CNPJ, gestora"
                  value={query}
                  onChange={pvChangeQuery}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon iconName="find" size={18} color={'primary'} />
                      </InputAdornment>
                    )
                  }}
                />
              </Stack>
              {!isUpLg && (
                <Stack alignItems="stretch">
                  <SearchFiltersMobileTrigger
                    appliedCount={appliedFiltersCount}
                    label="Filtros"
                    onClick={pvOpenMobileFilters}
                  />
                </Stack>
              )}
            </Stack>
            <Divider />
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                width: '100%',
                py: 1,
                overflow: 'hidden'
              }}>
              <ComparadorFundosResultsList
                error={error}
                filters={appliedFilters}
                hasMore={hasMore}
                loading={loading}
                loadingMore={loadingMore}
                loadMoreError={loadMoreError}
                results={results}
                onFocusItem={setFocusedId}
                onLoadMore={pvLoadMore}
                onRetryLoadMore={pvLoadMore}
                onSelectItem={pvSelectFundo}
                selectedFundosIds={selectedFundosIds}
                sort={sort}
                totalItems={totalItems}
                sortComponent={<ComparadorFundosSortButton sort={sort} onChange={setSort} />}
              />
            </Stack>
          </Stack>
          {isUpMd && (
            <>
              <Divider orientation="vertical" flexItem />
              <Stack
                sx={{
                  px: 3,
                  py: 3,
                  minWidth: 360,
                  width: 400,
                  minHeight: 0,
                  height: { md: '100%' }
                }}
                alignItems="stretch">
                {results.length > 0 && (
                  <ComparadorFundosFocusedItem
                    focusedItem={focusedItem}
                    isSelected={selectedFundosIds.includes(focusedItem?.id)}
                    maxSelected={maxSelected}
                    reachedLimit={reachedLimit}
                    results={results}
                  />
                )}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      {!isUpLg && (
        <SearchFiltersMobileDialog
          id="comparador-fundos-filtros"
          title="Filtros"
          open={mobileFiltersOpen}
          onClose={pvCloseMobileFilters}
          actions={
            <SearchFiltersActions
              applyDisabled={applyDisabled}
              onApply={pvApplyDraftFilters}
              onClear={pvClearDraftFilters}
            />
          }>
          <ComparadorFundosFilters
            appliedFilters={appliedFilters}
            filters={draftFilters}
            onChange={setDraftFilters}
            onValidityChange={pvChangeFilterValidity}
          />
        </SearchFiltersMobileDialog>
      )}
    </Dialog>
  );
});

export default ComparadorFundosBuscarDialog;
